'use client'

import { useCallback, useEffect, useRef } from 'react'

// A one-shot latch for a control that fires an event and then navigates away.
// `trackBeforeNavigating` defers the navigation by up to the delivery budget, leaving the
// control live in the meantime — so a double-tap, the likely case on mobile, would send
// two events and navigate twice.
//
// The latch is released on `pageshow`, because the navigation is a hard one: iOS Safari
// restores this component from the bfcache with its refs intact when the user comes back,
// and a latch that stayed closed would leave the control permanently dead.
export function useNavigationLatch() {
  const latchedRef = useRef(false)

  useEffect(() => {
    function pageShowHandler() {
      latchedRef.current = false
    }

    window.addEventListener('pageshow', pageShowHandler)
    return () => window.removeEventListener('pageshow', pageShowHandler)
  }, [])

  // Returns true for the first caller only; the handler should bail on false.
  return useCallback(() => {
    if (latchedRef.current) {
      return false
    }

    latchedRef.current = true

    return true
  }, [])
}
